import API_BASE from '../config';
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CreditCard, Building2, ShieldCheck, Crown, Lock,
  KeyRound, CheckCircle2, AlertTriangle,
  ChevronRight, Loader2, Eye, EyeOff, X, Zap,
  Smartphone, Copy, Trash2, Plus, RefreshCw, Key,
  ShieldAlert, Globe, History, Users2, FileCheck,
  FileBarChart
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// ─── Plan data ────────────────────────────────────────────────────────────────

const plans = [
  {
    tier:         "starter",
    label:        "Starter",
    monthlyPrice: 199,
    annualPrice:  159,
    color:        "text-slate-600",
    features:     [
      "1 Entity Assessment",
      "Standard 13-page Audit Report",
      "Basic Threat Detection",
      "Regulatory Compliance Radar",
    ],
  },
  {
    tier:         "professional",
    label:        "Professional",
    monthlyPrice: 499,
    annualPrice:  399,
    color:        "text-blue-600",
    features:     [
      "2 Entity Assessments",
      "Monte Carlo Confidence Intervals",
      "Full Actuarial Risk Engine",
      "Dark Web Credential Scan",
      "Vendor Risk Management",
      "Insurance Intelligence",
    ],
  },
  {
    tier:         "enterprise",
    label:        "Enterprise",
    monthlyPrice: 999,
    annualPrice:  799,
    color:        "text-amber-500",
    features:     [
      "5 Entity Assessments",
      "19-Slide Board Intelligence Deck",
      "Compliance Intelligence",
      "Insurance Intelligence",
      "Sector Benchmarking",
      "Priority Support",
    ],
  },
];

// ─── Upgrade Transparency Modal ───────────────────────────────────────────────

function UpgradeModal({
  from, to, billing, onConfirm, onCancel, isLoading,
}: {
  from:      typeof plans[0];
  to:        typeof plans[0];
  billing:   "monthly" | "annual";
  onConfirm: () => void;
  onCancel:  () => void;
  isLoading: boolean;
}) {
  const price = billing === "annual" ? to.annualPrice : to.monthlyPrice;
  const today = new Date();
  const next  = new Date(today);
  next.setMonth(next.getMonth() + (billing === "annual" ? 12 : 1));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[3rem] max-w-md w-full overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="bg-amber-50 border-b border-amber-100 px-10 py-7 flex items-start gap-4">
          <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-base font-black text-white uppercase tracking-tight">
              Upgrade Confirmation
            </h3>
            <p className="text-xs font-bold text-slate-500 mt-1 uppercase tracking-widest">
              Please read before confirming
            </p>
          </div>
          <button onClick={onCancel} className="ml-auto text-slate-300 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-10 py-8 space-y-6">
          {/* Plan transition */}
          <div className="flex items-center gap-3">
            <span className="px-4 py-2 bg-slate-100 rounded-xl text-xs font-black uppercase tracking-widest text-slate-600">
              {from.label}
            </span>
            <ChevronRight size={16} className="text-slate-300" />
            <span className="px-4 py-2 bg-red-600 rounded-xl text-xs font-black uppercase tracking-widest text-white">
              {to.label}
            </span>
          </div>

          {/* Terms */}
          <div className="space-y-3">
            {[
              { icon: CheckCircle2, color: "text-emerald-500", text: `Your new plan activates immediately` },
              { icon: CheckCircle2, color: "text-emerald-500", text: `You will be charged $${price.toLocaleString()} today` },
              { icon: CheckCircle2, color: "text-emerald-500", text: `Your billing date resets to ${today.toLocaleDateString("en", { day: "2-digit", month: "short", year: "numeric" })}` },
              { icon: AlertTriangle, color: "text-amber-500", text: `Remaining credit from your current plan is not carried over` },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <item.icon size={15} className={`${item.color} flex-shrink-0 mt-0.5`} />
                <p className="text-sm font-medium text-slate-200">{item.text}</p>
              </div>
            ))}
          </div>

          {/* Next billing */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">
              Next Billing Date
            </p>
            <p className="text-sm font-black text-slate-900">
              {next.toLocaleDateString("en", { day: "2-digit", month: "long", year: "numeric" })}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-10 pb-10 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-4 rounded-2xl font-black text-[11px] uppercase
              tracking-widest text-slate-400 border-2 border-slate-100
              hover:border-slate-300 hover:text-slate-200 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 py-4 rounded-2xl font-black text-[11px] uppercase
              tracking-widest bg-red-600 text-white hover:bg-slate-900
              transition-all shadow-lg shadow-red-100 disabled:opacity-50
              flex items-center justify-center gap-2"
          >
            {isLoading
              ? <><Loader2 size={14} className="animate-spin" /> Processing...</>
              : "I Understand — Confirm"
            }
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Entity Node ──────────────────────────────────────────────────────────────

function EntityNode({ entity }: { entity: any }) {
  return (
    <div className="flex items-center justify-between p-5 bg-white/5 rounded-2xl border border-white/10 hover:border-red-500/30 transition-all">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center
          justify-center text-red-600">
          <Building2 size={20} />
        </div>
        <div>
          <p className="text-sm font-black text-white uppercase tracking-tight">
            {entity.company_name}
          </p>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
            {entity.domain || "Domain not set"}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100
        text-emerald-600 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest">
        <ShieldCheck size={12} />
        Active
      </div>
    </div>
  );
}

// =============================================================================
// MFA SECTION COMPONENT
// =============================================================================

function MfaSection({ isEnterprise }: { isEnterprise: boolean }) {
  const [step,       setStep]       = useState<"idle" | "setup" | "confirm" | "done">("idle");
  const [qrCode,     setQrCode]     = useState("");
  const [secret,     setSecret]     = useState("");
  const [code,       setCode]       = useState("");
  const [loading,    setLoading]    = useState(false);
  const [msg,        setMsg]        = useState("");
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [disablePass,setDisablePass]= useState("");

  useQuery({
    queryKey: ["mfa-status"],
    queryFn:  () => axios.get(`${API_BASE}/api/auth/mfa/status`, { headers: authH() })
                        .then(r => { setMfaEnabled(r.data.mfa_enabled); return r.data; }),
    enabled: !!localStorage.getItem("token"),
  });

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/api/auth/mfa/setup`, {}, { headers: authH() });
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setStep("setup");
    } catch { setMsg("Setup failed. Please try again."); }
    finally { setLoading(false); }
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/api/auth/mfa/verify-setup`, { code }, { headers: authH() });
      setStep("done");
      setMfaEnabled(true);
      setMsg("MFA enabled successfully. Your account is now protected.");
    } catch { setMsg("Invalid code. Please try again."); }
    finally { setLoading(false); }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/api/auth/mfa/disable`, { password: disablePass }, { headers: authH() });
      setMfaEnabled(false);
      setStep("idle");
      setDisablePass("");
      setMsg("MFA disabled.");
    } catch { setMsg("Invalid password."); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[3rem] border border-white/10 overflow-hidden">
      <div className="px-10 py-8 border-b border-slate-50">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 bg-slate-900 rounded-2xl flex items-center justify-center shadow-lg">
            <Smartphone size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-black text-white uppercase tracking-tight">
              Two-Factor Authentication
            </h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
              Google Authenticator / Authy
            </p>
          </div>
          <span className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border ${
            mfaEnabled
              ? "bg-emerald-50 text-emerald-600 border-emerald-100"
              : "bg-red-50 text-red-600 border-red-100"
          }`}>
            {mfaEnabled ? "✓ Enabled" : "Not Enabled"}
          </span>
        </div>
      </div>

      <div className="px-10 py-8">
        {msg && (
          <div className={`mb-6 p-4 rounded-2xl text-xs font-bold flex items-center gap-3 ${
            msg.includes("success") || msg.includes("enabled")
              ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
              : "bg-red-50 text-red-600 border border-red-100"
          }`}>
            {msg.includes("success") || msg.includes("enabled")
              ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
            {msg}
          </div>
        )}

        {/* Not set up yet */}
        {!mfaEnabled && step === "idle" && (
          <div className="max-w-md">
            <p className="text-sm font-medium text-slate-300 leading-relaxed mb-6">
              Add an extra layer of security to your account. After setup, you will need
              your authenticator app every time you log in.
            </p>
            <button onClick={handleSetup} disabled={loading}
              className="flex items-center gap-2 bg-red-600 text-white px-8 py-4
                rounded-2xl font-black text-[10px] uppercase tracking-widest
                hover:bg-slate-900 transition-all shadow-lg shadow-red-100 disabled:opacity-50">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Smartphone size={14} />}
              Enable Two-Factor Authentication
            </button>
          </div>
        )}

        {/* QR Code step */}
        {step === "setup" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl">
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">
                Step 1 — Scan with Authenticator App
              </p>
              <div className="bg-white/10 border border-white/20 rounded-2xl p-4 inline-block">
                <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" />
              </div>
              <p className="text-[10px] font-bold text-slate-400 mt-3">
                Or enter manually: <span className="font-black text-slate-200 text-[9px] tracking-widest">{secret}</span>
              </p>
            </div>
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">
                Step 2 — Enter the 6-digit code
              </p>
              <input
                value={code} onChange={e => setCode(e.target.value)}
                maxLength={6} placeholder="000000"
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4
                  font-black text-2xl tracking-[0.4em] text-center text-slate-900
                  focus:outline-none focus:border-red-500 transition-all mb-4"
              />
              <button onClick={handleVerify} disabled={loading || code.length !== 6}
                className="w-full py-4 bg-red-600 text-white rounded-2xl font-black text-[10px]
                  uppercase tracking-widest hover:bg-slate-900 transition-all disabled:opacity-50
                  flex items-center justify-center gap-2">
                {loading ? <Loader2 size={14} className="animate-spin" /> : "Verify & Activate"}
              </button>
              <button onClick={() => setStep("idle")}
                className="w-full py-3 mt-2 text-slate-400 font-black text-[10px]
                  uppercase tracking-widest hover:text-slate-200 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Already enabled */}
        {mfaEnabled && step !== "setup" && (
          <div className="max-w-md">
            <p className="text-sm font-medium text-slate-300 leading-relaxed mb-6">
              Two-factor authentication is active. Enter your password below to disable it.
            </p>
            <div className="flex gap-3">
              <input
                type="password" placeholder="Enter your password"
                value={disablePass} onChange={e => setDisablePass(e.target.value)}
                className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-5 py-4
                  text-sm font-bold text-slate-900 focus:outline-none focus:border-red-500
                  transition-all"
              />
              <button onClick={handleDisable} disabled={loading || !disablePass}
                className="px-6 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px]
                  uppercase tracking-widest hover:bg-red-600 transition-all disabled:opacity-50
                  flex items-center gap-2">
                {loading ? <Loader2 size={14} className="animate-spin" /> : "Disable"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// =============================================================================
// API KEYS SECTION COMPONENT
// =============================================================================

function ApiKeysSection({ isEnterprise, isProfessional }: { isEnterprise: boolean; isProfessional: boolean }) {
  const queryClient               = useQueryClient();
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyValue, setNewKeyValue] = useState("");
  const [showNewKey, setShowNewKey] = useState(false);
  const [copied, setCopied]         = useState(false);

  const { data: apiKeys = [] } = useQuery({
    queryKey: ["api-keys"],
    queryFn:  () => axios.get(`${API_BASE}/api/keys`, { headers: authH() }).then(r => r.data),
    enabled:  !!localStorage.getItem("token") && (isEnterprise || isProfessional),
  });

  const generateMutation = useMutation({
    mutationFn: (name: string) =>
      axios.post(`${API_BASE}/api/keys/generate`, { name }, { headers: authH() })
           .then(r => r.data),
    onSuccess: (data) => {
      setNewKeyValue(data.key);
      setShowNewKey(true);
      setNewKeyName("");
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: number) =>
      axios.delete(`${API_BASE}/api/keys/${id}`, { headers: authH() }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["api-keys"] }),
  });

  const handleCopy = () => {
    navigator.clipboard.writeText(newKeyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[3rem] border border-white/10 overflow-hidden">
      <div className="px-10 py-8 border-b border-slate-50">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 bg-slate-900 rounded-2xl flex items-center justify-center shadow-lg">
            <Key size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-black text-white uppercase tracking-tight">
              API Access
            </h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
              Integrate VicShield data into PowerBI, Splunk & custom dashboards
            </p>
          </div>
          {!isProfessional && !isEnterprise && (
            <span className="px-4 py-2 rounded-xl text-[9px] font-black uppercase
              tracking-widest bg-blue-50 text-blue-600 border border-blue-100 flex items-center gap-1.5">
              <Crown size={11} /> Professional+
            </span>
          )}
        </div>
      </div>

      <div className="px-10 py-8">
        {!isProfessional && !isEnterprise ? (
          <div className="max-w-md">
            <p className="text-sm font-medium text-slate-500 leading-relaxed mb-5">
              API Access allows you to programmatically pull your VicShield risk data
              into any external dashboard or security platform using a secret API key.
            </p>
            <div className="space-y-3 mb-6">
              {[
                { icon: Globe, text: "Pull risk scores into PowerBI or Tableau" },
                { icon: ShieldAlert, text: "Feed threat data into Splunk or SIEM" },
                { icon: RefreshCw, text: "Automate weekly risk score reports" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <item.icon size={14} className="text-slate-400 flex-shrink-0" />
                  <span className="text-xs font-bold text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={async () => {
                  const token = localStorage.getItem("token");
                  const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({ tier: "professional", billing: "monthly" }),
                  });
                  const data = await res.json();
                  if (data.checkout_url) document.location.assign(data.checkout_url);
                }}
                className="inline-flex items-center gap-2 bg-blue-600 text-white
                  px-7 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest
                  hover:bg-blue-700 transition-all shadow-lg">
                Upgrade to Professional <ChevronRight size={14} />
              </button>
              <button
                onClick={async () => {
                  const token = localStorage.getItem("token");
                  const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({ tier: "enterprise", billing: "monthly" }),
                  });
                  const data = await res.json();
                  if (data.checkout_url) document.location.assign(data.checkout_url);
                }}
                className="inline-flex items-center gap-2 bg-amber-500 text-white
                  px-7 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest
                  hover:bg-amber-600 transition-all shadow-lg">
                Upgrade to Enterprise <ChevronRight size={14} />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* New key just generated — show once */}
            <AnimatePresence>
              {showNewKey && newKeyValue && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6"
                >
                  <p className="text-[9px] font-black uppercase tracking-widest text-amber-700 mb-3 flex items-center gap-2">
                    <AlertTriangle size={13} />
                    Copy this key now — it will not be shown again
                  </p>
                  <div className="flex items-center gap-3">
                    <code className="flex-1 text-xs font-black text-emerald-400 bg-white/5
                      border border-amber-200 rounded-xl px-4 py-3 truncate">
                      {newKeyValue}
                    </code>
                    <button onClick={handleCopy}
                      className="flex items-center gap-2 px-4 py-3 bg-amber-600 text-white
                        rounded-xl font-black text-[10px] uppercase tracking-widest
                        hover:bg-amber-700 transition-all flex-shrink-0">
                      <Copy size={13} />
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <button onClick={() => { setShowNewKey(false); setNewKeyValue(""); }}
                    className="mt-3 text-[10px] font-black uppercase tracking-widest
                      text-amber-600 hover:text-amber-800 transition-colors">
                    I have saved this key — dismiss
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Available endpoints */}
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-4">
                Available API Endpoints
              </p>
              <div className="space-y-2 mb-6">
                {[
                  { method: "GET", path: "/v1/dashboard",         desc: "All data in one call — best for most platforms" },
                  { method: "GET", path: "/v1/risk-score",        desc: "Current risk score & exposure" },
                  { method: "GET", path: "/v1/compliance-status", desc: "Regulatory compliance status" },
                  { method: "GET", path: "/v1/vendors",           desc: "Vendor risk list & scores" },
                  { method: "GET", path: "/v1/history",           desc: "Risk score history trend" },
                ].map((ep, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-[8px] font-black bg-emerald-100 text-emerald-700
                      px-2 py-1 rounded-lg uppercase tracking-widest flex-shrink-0">
                      {ep.method}
                    </span>
                    <code className="text-[10px] font-black text-slate-200">{ep.path}</code>
                    <span className="text-[10px] font-medium text-slate-400">{ep.desc}</span>
                  </div>
                ))}
              </div>

              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-4">
                How to connect — works with any platform
              </p>

              <div className="space-y-3">
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-300 mb-1">
                    Method 1 — URL Parameter
                  </p>
                  <p className="text-[10px] text-slate-400 mb-2">
                    Works with PowerBI, Tableau, Excel, Google Sheets, Zapier, Make.com and any no-code tool that accepts a web URL
                  </p>
                  <code className="text-[9px] font-bold text-slate-200 bg-black/20 px-3 py-2 rounded-lg block break-all">
                    https://api.vicshield.com/v1/dashboard?api_key=YOUR-FULL-API-KEY
                  </code>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-300 mb-1">
                    Method 2 — Request Header
                  </p>
                  <p className="text-[10px] text-slate-400 mb-2">
                    Works with Splunk, SIEM platforms, custom integrations and any tool that supports HTTP headers
                  </p>
                  <div className="space-y-2">
                    <div className="text-[9px] font-bold text-slate-200 bg-black/20 px-3 py-2 rounded-lg">
                      URL: https://api.vicshield.com/v1/dashboard
                    </div>
                    <div className="text-[9px] font-bold text-emerald-400 bg-black/20 px-3 py-2 rounded-lg">
                      Header: X-API-Key: YOUR-FULL-API-KEY
                    </div>

                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-300 mb-1">
                    Method 3 — Code
                  </p>
                  <p className="text-[10px] text-slate-400 mb-2">
                    Works with JavaScript, Python, R, or any programming language
                  </p>
                  <div className="space-y-2">
                    <div className="text-[9px] font-bold text-emerald-400 bg-black/20 px-3 py-2 rounded-lg">
                      {`fetch('https://api.vicshield.com/v1/dashboard', {`}
                    </div>
                    <div className="text-[9px] font-bold text-emerald-400 bg-black/20 px-3 py-2 rounded-lg">
                      {`  headers: { 'X-API-Key': 'YOUR-FULL-API-KEY' }`}
                    </div>
                    <div className="text-[9px] font-bold text-emerald-400 bg-black/20 px-3 py-2 rounded-lg">
                      {`})`}
                    </div>
                  </div>
                </div>

                <p className="text-[9px] font-bold text-slate-500">
                  Replace YOUR-FULL-API-KEY with the key shown when generated above. Keys are displayed once only — generate a new one if needed.
                </p>
              </div>
            </div>

            {/* Generate new key */}
            <div className="flex gap-3">
              <input
                value={newKeyName}
                onChange={e => setNewKeyName(e.target.value)}
                placeholder="Key name (e.g. PowerBI Integration)"
                className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-5 py-4
                  text-sm font-bold text-slate-900 placeholder:text-slate-300
                  placeholder:font-normal focus:outline-none focus:border-red-500 transition-all"
              />
              <button
                onClick={() => generateMutation.mutate(newKeyName || "API Key")}
                disabled={generateMutation.isPending}
                className="flex items-center gap-2 bg-red-600 text-white px-7 py-4
                  rounded-2xl font-black text-[10px] uppercase tracking-widest
                  hover:bg-slate-900 transition-all shadow-lg shadow-red-100
                  disabled:opacity-50 flex-shrink-0"
              >
                {generateMutation.isPending
                  ? <Loader2 size={14} className="animate-spin" />
                  : <><Plus size={14} /> Generate Key</>
                }
              </button>
            </div>

            {/* Existing keys list */}
            {apiKeys.length > 0 && (
              <div className="space-y-3">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                  Active Keys ({apiKeys.length}/5)
                </p>
                {apiKeys.map((k: any) => (
                  <div key={k.id}
                    className="flex items-center justify-between p-5 bg-white/5 rounded-2xl border border-white/10 hover:border-red-500/30 transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-9 h-9 bg-slate-900 rounded-xl flex items-center
                        justify-center">
                        <Key size={16} className="text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-black text-white uppercase tracking-tight">
                          {k.name}
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 font-mono mt-0.5">
                          {k.prefix}••••••••••••••••••••••••
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                          Created
                        </p>
                        <p className="text-[10px] font-bold text-slate-300">
                          {new Date(k.created_at).toLocaleDateString("en", {
                            day: "2-digit", month: "short", year: "numeric"
                          })}
                        </p>
                        {k.last_used && (
                          <p className="text-[9px] font-bold text-emerald-500 mt-0.5">
                            Last used {new Date(k.last_used).toLocaleDateString("en", {
                              day: "2-digit", month: "short"
                            })}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => revokeMutation.mutate(k.id)}
                        disabled={revokeMutation.isPending}
                        className="p-2.5 text-slate-300 hover:text-red-600 hover:bg-red-50
                          rounded-xl transition-all"
                        title="Revoke key"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {apiKeys.length === 0 && (
              <div className="py-10 text-center border-2 border-dashed border-white/10 rounded-2xl">
                <Key size={28} className="mx-auto text-slate-200 mb-3" />
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
                  No API keys generated yet
                </p>
                <p className="text-[10px] font-bold text-slate-300 mt-1 uppercase tracking-widest">
                  Generate your first key above
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// =============================================================================
// AUDIT TRAIL COMPONENT
// =============================================================================

const eventIcons: Record<string, { icon: any; color: string }> = {
  assessment: { icon: FileCheck,    color: "text-blue-600 bg-blue-50"     },
  report:     { icon: FileBarChart, color: "text-purple-600 bg-purple-50" },
  vendor:     { icon: Building2,    color: "text-amber-600 bg-amber-50"   },
  team:       { icon: Users2,       color: "text-emerald-600 bg-emerald-50"},
  auth:       { icon: Lock,         color: "text-red-600 bg-red-50"       },
  api:        { icon: Key,          color: "text-slate-600 bg-slate-100"  },
};

function AuditTrail() {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["audit-log"],
    queryFn:  () =>
      axios.get(`${API_BASE}/api/audit/log?limit=50`, { headers: authH() })
           .then(r => r.data),
    enabled:  !!localStorage.getItem("token"),
    refetchOnWindowFocus: true,
  });

  return (
    <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[3rem] border border-white/10 overflow-hidden">
      <div className="px-10 py-8 border-b border-slate-50">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 bg-slate-900 rounded-2xl flex items-center justify-center shadow-lg">
            <History size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-black text-white uppercase tracking-tight">
              Audit Trail
            </h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
              Complete immutable activity log
            </p>
          </div>
          <span className="ml-auto text-[9px] font-black uppercase tracking-widest
            text-slate-400 bg-white/10 px-3 py-1.5 rounded-full border border-white/10">
            Last 50 events
          </span>
        </div>
      </div>
      <div className="px-10 py-6">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 size={28} className="animate-spin text-red-600" />
          </div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center border-2 border-dashed border-white/10 rounded-2xl">
            <History size={32} className="mx-auto text-slate-200 mb-3" />
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
              No activity recorded yet
            </p>
            <p className="text-[10px] font-bold text-slate-300 mt-1 uppercase tracking-widest">
              Events appear here as you use the platform
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log: any) => {
              const style = eventIcons[log.event_type] || eventIcons.auth;
              const Icon  = style.icon;
              return (
                <motion.div key={log.id}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-start gap-4 p-5 bg-slate-50 rounded-2xl
                    border border-slate-100 hover:border-red-100 hover:bg-white transition-all"
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center
                    flex-shrink-0 ${style.color}`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-slate-900 leading-tight">
                      {log.description}
                    </p>
                    {log.performed_by && (
                      <p className="text-[10px] font-bold text-slate-400 mt-0.5">
                        by {log.performed_by}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[10px] font-black text-slate-200 uppercase tracking-wide">
                      {log.date_label}
                    </p>
                    <p className="text-[9px] font-bold text-slate-400 mt-0.5">{log.time_label}</p>
                    <span className={`text-[8px] font-black uppercase tracking-widest
                      px-2 py-0.5 rounded-full mt-1 inline-block ${style.color}`}>
                      {log.event_type}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// =============================================================================
// TEAM MEMBERS COMPONENT
// =============================================================================

function TeamMembers({ tier }: { tier: string }) {
  const queryClient                     = useQueryClient();
  const [inviteEmail, setInviteEmail]   = useState("");
  const [inviteRole,  setInviteRole]    = useState("analyst");
  const [msg,         setMsg]           = useState("");

  const teamLimit = tier === "enterprise" ? 10 : tier === "professional" ? 2 : 0;

  const { data: members = [] } = useQuery({
    queryKey: ["team-members"],
    queryFn:  () =>
      axios.get(`${API_BASE}/api/team`, { headers: authH() }).then(r => r.data),
    enabled:  !!localStorage.getItem("token") && teamLimit > 0,
  });

  const inviteMutation = useMutation({
    mutationFn: () =>
      axios.post(`${API_BASE}/api/team/invite`,
        { email: inviteEmail, role: inviteRole }, { headers: authH() }).then(r => r.data),
    onSuccess: () => {
      setMsg(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      queryClient.invalidateQueries({ queryKey: ["audit-log"] });
    },
    onError: (err: any) => setMsg(err.response?.data?.detail || "Invite failed."),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) =>
      axios.delete(`${API_BASE}/api/team/${id}`, { headers: authH() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      queryClient.invalidateQueries({ queryKey: ["audit-log"] });
    },
  });

  const roleColors: Record<string, string> = {
    admin:   "bg-red-50 text-red-700 border-red-100",
    analyst: "bg-blue-50 text-blue-700 border-blue-100",
    viewer:  "bg-slate-50 text-slate-600 border-slate-100",
  };

  const statusColors: Record<string, string> = {
    active:  "text-emerald-600",
    pending: "text-amber-600",
    revoked: "text-slate-300",
  };

  return (
    <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[3rem] border border-white/10 overflow-hidden">
      <div className="px-10 py-8 border-b border-slate-50">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 bg-slate-900 rounded-2xl flex items-center justify-center shadow-lg">
            <Users2 size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-black text-white uppercase tracking-tight">
              Team Members
            </h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
              {members.length} / {teamLimit === 0 ? "0" : teamLimit} members
            </p>
          </div>
          {teamLimit === 0 && (
            <span className="px-4 py-2 rounded-xl text-[9px] font-black uppercase
              tracking-widest bg-amber-50 text-amber-600 border border-amber-100
              flex items-center gap-1.5">
              <Crown size={11} /> Professional+
            </span>
          )}
        </div>
      </div>
      <div className="px-10 py-8 space-y-6">

        {/* Role explanation cards */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { role: "Admin",   desc: "Full access + manage team",     color: "border-red-500/30 bg-red-900/20"    },
            { role: "Analyst", desc: "Run assessments + view reports", color: "border-blue-500/30 bg-blue-900/20" },
            { role: "Viewer",  desc: "Read-only dashboard access",     color: "border-white/10 bg-white/5"        },
          ].map((r, i) => (
            <div key={i} className={`p-4 rounded-2xl border-2 ${r.color}`}>
              <p className="text-[9px] font-black uppercase tracking-widest text-white mb-1">
                {r.role}
              </p>
              <p className="text-[10px] font-bold text-slate-300">{r.desc}</p>
            </div>
          ))}
        </div>

        {/* Feedback */}
        <AnimatePresence>
          {msg && (
            <motion.div
              initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className={`p-4 rounded-2xl text-xs font-bold flex items-center gap-3 ${
                msg.includes("sent")
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                  : "bg-red-50 text-red-600 border border-red-100"
              }`}
            >
              {msg.includes("sent") ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
              {msg}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Invite form */}
        {teamLimit > 0 && members.length < teamLimit && (
          <div className="flex gap-3">
            <input
              value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              placeholder="colleague@company.com"
              className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-5 py-4
                text-sm font-bold text-white placeholder:text-slate-500
                placeholder:font-normal focus:outline-none focus:border-red-500 transition-all"
            />
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-2xl px-5 py-4
                text-sm font-bold text-white focus:outline-none focus:border-red-500
                transition-all cursor-pointer bg-[#1e293b]">
              <option value="viewer">Viewer</option>
              <option value="analyst">Analyst</option>
              <option value="admin">Admin</option>
            </select>
            <button
              onClick={() => inviteMutation.mutate()}
              disabled={!inviteEmail.trim() || inviteMutation.isPending}
              className="flex items-center gap-2 bg-red-600 text-white px-7 py-4
                rounded-2xl font-black text-[10px] uppercase tracking-widest
                hover:bg-slate-900 transition-all shadow-lg shadow-red-100
                disabled:opacity-50 flex-shrink-0"
            >
              {inviteMutation.isPending
                ? <Loader2 size={14} className="animate-spin" />
                : <><Plus size={14} /> Invite</>
              }
            </button>
          </div>
        )}

        {/* Members list */}
        {members.length > 0 ? (
          <div className="space-y-3">
            {members.map((m: any) => (
              <div key={m.id}
                className="flex items-center justify-between p-5 bg-slate-50 rounded-2xl
                  border border-slate-100 hover:border-red-100 transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center
                    justify-center text-slate-600 font-black text-sm uppercase">
                    {m.email[0]}
                  </div>
                  <div>
                    <p className="text-sm font-black text-slate-900">{m.full_name || m.email}</p>
                    {m.full_name && (
                      <p className="text-[10px] font-bold text-slate-400">{m.email}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase
                    tracking-widest border ${roleColors[m.role] || roleColors.viewer}`}>
                    {m.role}
                  </span>
                  <span className={`text-[9px] font-black uppercase tracking-widest ${
                    statusColors[m.status] || "text-slate-400"
                  }`}>
                    {m.status}
                  </span>
                  <button onClick={() => removeMutation.mutate(m.id)}
                    className="p-2.5 text-slate-300 hover:text-red-600 hover:bg-red-50
                      rounded-xl transition-all">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : teamLimit > 0 ? (
          <div className="py-10 text-center border-2 border-dashed border-white/10 rounded-2xl">
            <Users2 size={28} className="mx-auto text-slate-200 mb-3" />
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
              No team members yet — invite colleagues above
            </p>
          </div>
        ) : (
          <div className="py-10 text-center border-2 border-dashed border-white/10 rounded-2xl">
            <Crown size={28} className="mx-auto text-amber-300 mb-3" />
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
              Team access from Professional plan
            </p>
            <a href="/settings" className="mt-4 inline-flex items-center gap-2 bg-red-600
              text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest
              hover:bg-slate-900 transition-all shadow-md">
              Upgrade Plan <ChevronRight size={13} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}


// =============================================================================

export default function Settings() {
  const [resetStep,       setResetStep]       = useState<1 | 2>(1);
  const [otp,             setOtp]             = useState("");
  const [newPassword,     setNewPassword]     = useState("");
  const [showPass,        setShowPass]        = useState(false);
  const [resetLoading,    setResetLoading]    = useState(false);
  const [resetMsg,        setResetMsg]        = useState("");

  // Upgrade state
  const [selectedPlan,    setSelectedPlan]    = useState<typeof plans[0] | null>(null);
  const [billing,         setBilling]         = useState<"monthly" | "annual">("monthly");
  const [upgradeLoading,  setUpgradeLoading]  = useState(false);
  const [showUpgradeModal,setShowUpgradeModal]= useState(false);

  const { data: user, isLoading, refetch } = useQuery({
    queryKey: ["me"],
    queryFn:  () =>
      axios.get(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }).then((res) => res.data),
    enabled: !!localStorage.getItem("token"),
  });

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
      <Loader2 size={32} className="animate-spin text-red-600" />
    </div>
  );

  const rawTier  = user?.subscription_tier?.toLowerCase() || "standard";
  const tier     = rawTier === "standard" ? "starter" : rawTier;
  const entities = user?.entities || [];
  const maxSlots = tier === "enterprise" ? "5" : tier === "professional" ? "2" : "1";
  const currentPlan = plans.find(p => p.tier === tier) || plans[0];

  // Upgradeable plans — only show plans above current tier
  const upgradablePlans = plans.filter(p => {
    const order: Record<string, number> = { starter: 0, professional: 1, enterprise: 2 };
    return (order[p.tier] ?? 0) > (order[tier] ?? 0);
  });

  // Password reset handlers
  const handleResetRequest = async () => {
    setResetLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/auth/reset-request`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setResetStep(2);
      setResetMsg("Verification code sent. Check your email.");
    } catch {
      setResetMsg("Request failed. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetConfirm = async () => {
    setResetLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/auth/reset-confirm`,
        { otp_code: otp, new_password: newPassword },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setResetMsg("Password updated successfully.");
      setResetStep(1);
      setOtp("");
      setNewPassword("");
    } catch {
      setResetMsg("Invalid code. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  // Upgrade handlers
  const handleSelectPlan = (plan: typeof plans[0]) => {
    setSelectedPlan(plan);
    setShowUpgradeModal(true);
  };

  const handleConfirmUpgrade = async () => {
    if (!selectedPlan) return;
    setUpgradeLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/stripe/create-checkout`,
        { tier: selectedPlan.tier, billing },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      // Open Stripe checkout in same tab
      const checkoutUrl = res.data.checkout_url;
      if (checkoutUrl) {
        document.location.assign(checkoutUrl);
      }
    } catch (err: any) {
      setResetMsg(
        err?.response?.data?.detail ||
        "Failed to start checkout. Please try again or contact support@vicshield.com"
      );
      setUpgradeLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 md:p-6 space-y-5 bg-[#f8fafc] min-h-screen font-['Inter']"
    >

      {/* PAGE HEADER */}
      <div>
        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-600 mb-1">
          Account Management
        </p>
        <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">
          Settings
        </h1>
      </div>

      {/* ── ROW 1: SUBSCRIPTION + ENTITIES ──────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Subscription card */}
        <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[3rem] border border-white/10 overflow-hidden
          flex flex-col">
          <div className="p-10 border-b border-slate-50">
            <div className="flex items-start justify-between mb-6">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                Current Plan
              </p>
              <Crown size={22} className={
                tier === "enterprise" ? "text-amber-400" :
                tier === "professional" ? "text-blue-400" : "text-slate-200"
              } />
            </div>
            <h2 className={`text-6xl font-black tracking-tighter uppercase mb-2 ${
              tier === "enterprise" ? "text-amber-500" :
              tier === "professional" ? "text-blue-600" : "text-slate-900"
            }`}>
              {currentPlan.label}
            </h2>
            <p className="text-xs font-bold text-slate-300 uppercase tracking-widest">
              ${(billing === "annual" ? currentPlan.annualPrice : currentPlan.monthlyPrice).toLocaleString()}/mo
              {billing === "annual" && (
                <span className="ml-2 text-emerald-500">· Annual billing (20% off)</span>
              )}
            </p>
          </div>

          <div className="p-10 flex-1 space-y-3">
            {currentPlan.features.map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
                <span className="text-xs font-bold text-slate-300">{f}</span>
              </div>
            ))}
          </div>

          <div className="px-10 pb-10 flex gap-3">
            <button
              onClick={async () => {
                try {
                  const res = await axios.post(
                    `${API_BASE}/api/stripe/create-portal`,
                    {},
                    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
                  );
                  window.location.href = res.data.portal_url;
                } catch (err: any) {
                  alert(err?.response?.data?.detail || "No active subscription found. Please subscribe first.");
                }
              }}
              className="flex-1 flex items-center justify-center gap-2 py-3.5
                rounded-2xl bg-slate-900 text-white font-black text-[10px] uppercase
                tracking-widest hover:bg-red-600 transition-all shadow-md">
              <CreditCard size={14} /> Billing Portal
            </button>
          </div>
        </div>

        {/* Entities card */}
        <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[3rem] border border-white/10 overflow-hidden flex flex-col">
          <div className="p-10 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                  Registered Entities
                </p>
                <h3 className="text-2xl font-black text-white tracking-tighter">
                  {entities.length}
                  <span className="text-slate-200 ml-1">/ {maxSlots}</span>
                </h3>
              </div>
              <div className="w-12 h-12 bg-white/10 border border-white/20
                rounded-2xl flex items-center justify-center">
                <Building2 size={22} className="text-slate-300" />
              </div>
            </div>
          </div>

          <div className="p-10 flex-1 space-y-3">
            {/* FIX: entities bug — was returning nothing, now renders EntityNode correctly */}
            {entities.length > 0 ? (
              entities.map((e: any, index: number) => (
                <EntityNode key={index} entity={e} />
              ))
            ) : (
              <div className="py-10 text-center border-2 border-dashed border-slate-100
                rounded-2xl">
                <Building2 size={28} className="mx-auto text-slate-200 mb-3" />
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
                  No entities linked
                </p>
                <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest mt-1">
                  Complete your risk assessment to register your first entity
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── UPGRADE SECTION ─────────────────────────────────────────── */}
      {upgradablePlans.length > 0 && (
        <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[3rem] border border-white/10 overflow-hidden">
          <div className="px-10 py-8 border-b border-slate-50">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-red-600 rounded-2xl flex items-center
                justify-center shadow-lg shadow-red-100">
                <Zap size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-black text-white uppercase tracking-tight">
                  Upgrade Your Plan
                </h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                  Unlock more intelligence capabilities
                </p>
              </div>

              {/* Billing toggle */}
              <div className="ml-auto flex items-center gap-1 bg-slate-50 border-2
                border-slate-100 rounded-xl p-1">
                {(["monthly", "annual"] as const).map((b) => (
                  <button
                    key={b}
                    onClick={() => setBilling(b)}
                    className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase
                      tracking-widest transition-all ${
                      billing === b
                        ? "bg-slate-900 text-white shadow-sm"
                        : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    {b}
                    {b === "annual" && billing !== "annual" && (
                      <span className="ml-1.5 bg-emerald-100 text-emerald-700
                        text-[8px] px-1.5 py-0.5 rounded-full">
                        -20%
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="p-10">
            <div className={`grid gap-6 ${
              upgradablePlans.length === 1 ? "grid-cols-1 max-w-sm" :
              upgradablePlans.length === 2 ? "grid-cols-1 md:grid-cols-2" :
              "grid-cols-1 md:grid-cols-3"
            }`}>
              {upgradablePlans.map((plan) => {
                const price = billing === "annual" ? plan.annualPrice : plan.monthlyPrice;
                return (
                  <div
                    key={plan.tier}
                    className={`rounded-3xl border-2 p-8 flex flex-col transition-all
                      hover:shadow-xl cursor-pointer group ${
                      plan.tier === "enterprise"
                        ? "bg-[#0f172a] border-[#0f172a] hover:border-red-600/40"
                        : "bg-white border-slate-100 hover:border-red-100"
                    }`}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h4 className={`text-xs font-black uppercase tracking-widest ${
                        plan.tier === "enterprise" ? "text-red-500" : "text-slate-400"
                      }`}>
                        {plan.label}
                      </h4>
                      {plan.tier === "enterprise" && (
                        <Crown size={16} className="text-amber-400" />
                      )}
                    </div>

                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className={`text-4xl font-black tracking-tighter ${
                          plan.tier === "enterprise" ? "text-white" : "text-slate-900"
                        }`}>
                          ${price}
                        </span>
                        <span className="text-sm font-bold text-slate-400">/mo</span>
                      </div>
                      {billing === "annual" && (
                        <p className="text-[10px] font-bold text-emerald-500 mt-1">
                          Save ${(plan.monthlyPrice - plan.annualPrice) * 12}/yr
                        </p>
                      )}
                    </div>

                    <ul className="space-y-2.5 mb-8 flex-1">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2.5">
                          <CheckCircle2 size={13} className="text-red-500 flex-shrink-0" />
                          <span className={`text-xs font-bold ${
                            plan.tier === "enterprise" ? "text-slate-300" : "text-slate-600"
                          }`}>
                            {f}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <button className={`w-full py-3.5 rounded-2xl font-black text-[10px]
                      uppercase tracking-widest transition-all flex items-center
                      justify-center gap-2 group-hover:gap-3 ${
                      plan.tier === "enterprise"
                        ? "bg-red-600 text-white hover:bg-white hover:text-red-600 shadow-lg shadow-red-900/30"
                        : "bg-slate-900 text-white hover:bg-red-600"
                    }`}>
                      Upgrade to {plan.label}
                      <ChevronRight size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Already on enterprise — no upgrade needed */}
      {tier === "enterprise" && (
        <div className="bg-[#0f172a] rounded-[3rem] p-10 flex items-center gap-6 border-2 border-[#0f172a]">
          <div className="w-14 h-14 bg-amber-400/10 border border-amber-400/20
            rounded-2xl flex items-center justify-center flex-shrink-0">
            <Crown size={26} className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-lg font-black text-white uppercase tracking-tight">
              You're on the highest tier
            </h3>
            <p className="text-sm font-medium text-slate-400 mt-1">
              You have access to every VicShield capability. Contact support for custom enterprise pricing.
            </p>
          </div>
          <a
            href="https://vicshield.com/contact"
            target="_blank"
            className="ml-auto bg-red-600 text-white px-6 py-3.5 rounded-2xl font-black
              text-[10px] uppercase tracking-widest hover:bg-white hover:text-red-600
              transition-all flex-shrink-0 whitespace-nowrap"
          >
            Contact Support
          </a>
        </div>
      )}

      {/* ── CREDENTIAL RESET ────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[3rem] border border-white/10 overflow-hidden">
        <div className="px-10 py-8 border-b border-slate-50">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 bg-slate-900 rounded-2xl flex items-center
              justify-center shadow-lg">
              <Lock size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-black text-white uppercase tracking-tight">
                Credential Reset
              </h3>
              <p className="text-[10px] font-bold text-red-600 uppercase tracking-widest mt-0.5">
                Multi-Factor Verification Required
              </p>
            </div>
          </div>
        </div>

        <div className="px-10 py-8">
          {/* Success / error message */}
          <AnimatePresence>
            {resetMsg && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`mb-6 p-4 rounded-2xl text-xs font-bold flex items-center gap-3 ${
                  resetMsg.includes("success") || resetMsg.includes("Successfully")
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                    : "bg-red-50 text-red-600 border border-red-100"
                }`}
              >
                {resetMsg.includes("success") || resetMsg.includes("Successfully")
                  ? <CheckCircle2 size={15} />
                  : <AlertTriangle size={15} />
                }
                {resetMsg}
              </motion.div>
            )}
          </AnimatePresence>

          {resetStep === 1 ? (
            <div className="max-w-md space-y-4">
              <p className="text-sm font-medium text-slate-300 leading-relaxed">
                To update your secure access credentials, VicShield requires a secondary
                out-of-band verification code sent to your registered email address.
              </p>
              <button
                onClick={handleResetRequest}
                disabled={resetLoading}
                className="flex items-center gap-2 bg-red-600 text-white px-8 py-4
                  rounded-2xl font-black text-[10px] uppercase tracking-widest
                  hover:bg-slate-900 transition-all shadow-lg shadow-red-100
                  disabled:opacity-50"
              >
                {resetLoading
                  ? <><Loader2 size={14} className="animate-spin" /> Sending...</>
                  : <><KeyRound size={14} /> Initiate Reset Stream</>
                }
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
              {/* OTP field */}
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Verification Code
                </label>
                <input
                  placeholder="000000"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl
                    px-5 py-4 font-black text-2xl tracking-[0.4em] text-center
                    text-slate-900 focus:outline-none focus:border-red-500 transition-all"
                />
              </div>

              {/* New password field */}
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl
                      px-5 py-4 pr-12 font-bold text-slate-900 text-sm
                      focus:outline-none focus:border-red-500 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400
                      hover:text-slate-200 transition-colors"
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Action buttons */}
              <div className="md:col-span-2 flex gap-3">
                <button
                  onClick={handleResetConfirm}
                  disabled={resetLoading || !otp || !newPassword}
                  className="flex-1 py-4 bg-slate-900 text-white rounded-2xl font-black
                    text-[10px] uppercase tracking-widest hover:bg-red-600
                    transition-all disabled:opacity-50 flex items-center
                    justify-center gap-2"
                >
                  {resetLoading
                    ? <><Loader2 size={14} className="animate-spin" /> Updating...</>
                    : "Confirm Update"
                  }
                </button>
                <button
                  onClick={() => { setResetStep(1); setResetMsg(""); }}
                  className="px-6 py-4 rounded-2xl text-slate-400 font-black text-[10px]
                    uppercase tracking-widest hover:text-slate-200 border-2 border-slate-100
                    hover:border-slate-200 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── MFA SECTION ─────────────────────────────────────────────── */}
      <MfaSection isEnterprise={tier === "enterprise"} />

      {/* ── API KEYS SECTION (Enterprise Only) ──────────────────────── */}
      <ApiKeysSection isEnterprise={tier === "enterprise"} isProfessional={tier === "professional"} />

      {/* ── TEAM MEMBERS ────────────────────────────────────────────── */}
      <TeamMembers tier={tier} />

      {/* ── AUDIT TRAIL ─────────────────────────────────────────────── */}
      <AuditTrail />

      {/* ── UPGRADE TRANSPARENCY MODAL ───────────────────────────────── */}
      <AnimatePresence>
        {showUpgradeModal && selectedPlan && (
          <UpgradeModal
            from={currentPlan}
            to={selectedPlan}
            billing={billing}
            onConfirm={handleConfirmUpgrade}
            onCancel={() => { setShowUpgradeModal(false); setSelectedPlan(null); }}
            isLoading={upgradeLoading}
          />
        )}
      </AnimatePresence>

    </motion.div>
  );
}
